import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Award, Check, ChevronLeft, Upload, Plus, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CoachApplicationForm() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    applicant_email: '',
    applicant_phone: '',
    city: '',
    country: '',
    specializations: [],
    certifications: [],
    experience_years: 0,
    bio: '',
    photo: '',
    hourly_rate: '',
    documents: []
  });
  
  const [newSpecialization, setNewSpecialization] = useState('');
  const [newCertification, setNewCertification] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/synergies/coaches/apply`, formData);
      setSubmitted(true);
      console.log('Application submitted:', response.data);
    } catch (error) {
      console.error('Error submitting application:', error);
      alert(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const addSpecialization = () => {
    if (newSpecialization.trim()) {
      setFormData({
        ...formData,
        specializations: [...formData.specializations, newSpecialization.trim().toLowerCase()]
      });
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (index) => {
    setFormData({
      ...formData,
      specializations: formData.specializations.filter((_, i) => i !== index)
    });
  };

  const addCertification = () => {
    if (newCertification.trim()) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, newCertification.trim()]
      });
      setNewCertification('');
    }
  };

  const removeCertification = (index) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((_, i) => i !== index)
    });
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-deep-dark flex items-center justify-center px-4">
        <div className="bg-dark-surface rounded-xl p-8 border border-premium-gold/50 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-premium-gold/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-premium-gold" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            Application Submitted!
          </h2>
          <p className="text-white/70 mb-6">
            Thank you for your interest in joining DIOS Synergies as a coach! 
            We'll review your application and get back to you within 2-3 business days.
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-premium-gold hover:bg-red-700 text-white font-bold tracking-wider py-3 rounded-lg transition-all duration-300"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-dark py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <div className="bg-dark-surface rounded-xl p-8 border border-premium-gold/30/50 mb-8">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-premium-gold/20 rounded-full flex items-center justify-center">
              <Award className="w-8 h-8 text-premium-gold" strokeWidth={1.5} />
            </div>
            <div>
              <h1 
                className="text-3xl font-bold text-white"
                style={{ fontFamily: 'Fraunces, serif' }}
              >
                Become a <span className="gold-gradient">coach partner</span>
              </h1>
              <p className="text-white/60">Join our global network of expert coaches</p>
            </div>
          </div>
          
          <div className="bg-deep-dark rounded-lg p-4 mb-6">
            <h3 className="text-white font-semibold mb-2">Why Join DIOS Synergies?</h3>
            <ul className="space-y-2 text-white/70 text-sm">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Access to global audience of athletes and fitness enthusiasts</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Flexible scheduling - set your own availability</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Secure payment processing through Razorpay</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-premium-gold" />
                <span>Marketing support and verified badge</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Application Form */}
        <form onSubmit={handleSubmit} className="bg-dark-surface rounded-xl p-8 border border-premium-gold/30/50 space-y-6">
          <h2 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
            Application form
          </h2>

          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white/90 border-b border-premium-gold/20 pb-2">
              Personal Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="Enter your first name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="Enter your last name"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Email Address *</label>
                <input
                  type="email"
                  value={formData.applicant_email}
                  onChange={(e) => setFormData({ ...formData, applicant_email: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="your@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.applicant_phone}
                  onChange={(e) => setFormData({ ...formData, applicant_phone: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="+1 234 567 8900"
                  required
                />
              </div>
            </div>
            
            <p className="text-xs text-soft-lavender italic">
              This information is kept private and will not be shared with students or other coaches.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">City *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. Mumbai, London"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/70 mb-2">Country *</label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. India, UK"
                  required
                />
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white/90 border-b border-premium-gold/20 pb-2">
              Professional information
            </h3>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Bio *</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                rows="4"
                placeholder="Tell us about your coaching experience, philosophy, and achievements..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Years of Experience *</label>
              <input
                type="number"
                min="0"
                value={formData.experience_years}
                onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Sports Specializations * (Max 3)</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newSpecialization}
                  onChange={(e) => setNewSpecialization(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialization())}
                  className="flex-1 px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. cricket, tennis, football"
                  disabled={formData.specializations.length >= 3}
                />
                <button
                  type="button"
                  onClick={addSpecialization}
                  disabled={formData.specializations.length >= 3}
                  className="px-4 py-3 bg-premium-gold hover:opacity-90 text-deep-dark rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.specializations.map((spec, idx) => (
                  <span key={idx} className="px-3 py-1 bg-deep-dark text-white rounded-lg flex items-center gap-2 capitalize">
                    {spec}
                    <button
                      type="button"
                      onClick={() => removeSpecialization(idx)}
                      className="text-premium-gold hover:opacity-80"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </span>
                ))}
              </div>
              {formData.specializations.length === 0 && (
                <p className="text-xs text-soft-lavender mt-1">Add at least one sport specialization</p>
              )}
              {formData.specializations.length >= 3 && (
                <p className="text-xs text-premium-gold mt-1">You can select up to 3 sports only.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Certifications</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newCertification}
                  onChange={(e) => setNewCertification(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                  className="flex-1 px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                  placeholder="e.g. BCCI Level 3 Coach, UEFA Pro License"
                />
                <button
                  type="button"
                  onClick={addCertification}
                  className="px-4 py-3 bg-premium-gold hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.certifications.map((cert, idx) => (
                  <span key={idx} className="px-3 py-1 bg-deep-dark text-white rounded-lg flex items-center gap-2 text-sm">
                    {cert}
                    <button
                      type="button"
                      onClick={() => removeCertification(idx)}
                      className="text-premium-gold hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Hourly Rate (₹) *</label>
              <input
                type="number"
                min="0"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                placeholder="e.g. 2000"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white/70 mb-2">Profile Photo URL (Optional)</label>
              <input
                type="url"
                value={formData.photo}
                onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                className="w-full px-4 py-3 min-h-[48px] bg-deep-dark border border-premium-gold/30 rounded-lg text-white text-base placeholder-white/40 focus:border-premium-gold focus:outline-none"
                placeholder="https://example.com/your-photo.jpg"
              />
              <p className="text-xs text-white/50 mt-1">Provide a URL to your professional photo</p>
            </div>
          </div>

          {/* Submit */}
          <div className="pt-6 border-t border-premium-gold/30/50">
            <button
              type="submit"
              disabled={loading || formData.specializations.length === 0}
              className="w-full bg-premium-gold hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold tracking-wider py-4 min-h-[48px] rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white/20 border-t-white"></div>
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <Award className="w-5 h-5" strokeWidth={1.5} />
                </>
              )}
            </button>
            <p className="text-xs text-white/50 text-center mt-4">
              By submitting this application, you agree to our Terms of Service and Privacy Policy
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CoachApplicationForm;
